import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { 
  fetchLessonsAsync,
  deleteLessonAsync,
  clearError, 
  setFilters,
  setPage,
  clearFilters
} from '../store/slices/lessonsSlice';

import LoadingSpinner from './LoadingSpinner';
import ConfirmationModal from './ConfirmationModal';
import { formatDateEuropean } from '../utils/dateUtils';
import './Students.css';
import './GroupForm.css';

const Lessons: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { lessons, loading, error, filters, pagination } = useAppSelector((state) => state.lessons);


  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; lessonId: number | null; lessonInfo: string }>({ isOpen: false, lessonId: null, lessonInfo: '' });
  const [searchInput, setSearchInput] = useState(filters.search);
  const [selectedGroupId, setSelectedGroupId] = useState<string>(filters.groupId?.toString() || '');
  const [selectedLevel, setSelectedLevel] = useState<string>(filters.level || '');
  const [dateFrom, setDateFrom] = useState<string>(filters.dateFrom || '');
  const [dateTo, setDateTo] = useState<string>(filters.dateTo || '');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'canceled'>(
    filters.canceled === undefined ? 'all' : filters.canceled ? 'canceled' : 'active'
  );
  const initialLoadRef = useRef(false);

  const levels = [
    { value: 'a1', label: 'A1' },
    { value: 'a2', label: 'A2' },
    { value: 'b1', label: 'B1' },
    { value: 'b2', label: 'B2' },
    { value: 'c1', label: 'C1' },
    { value: 'c2', label: 'C2' },
  ];



  // Load lessons with current filters
  const loadLessons = useCallback((page?: number) => {
    const params: { dateFrom?: string; dateTo?: string; groupId?: number; canceled?: boolean; page?: number; limit?: number } = {};

    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (selectedGroupId) params.groupId = parseInt(selectedGroupId);
    if (statusFilter !== 'all') params.canceled = statusFilter === 'canceled';
    params.page = page || pagination.currentPage;
    params.limit = pagination.itemsPerPage;

    dispatch(fetchLessonsAsync(params));
  }, [dispatch, dateFrom, dateTo, selectedGroupId, statusFilter, pagination.currentPage, pagination.itemsPerPage]);

  // Load lessons on mount (without any date filter by default)
  useEffect(() => {
    if (!initialLoadRef.current) {
      loadLessons();
      initialLoadRef.current = true;
    }
  }, [loadLessons]);

  const handleSearch = useCallback(() => {
    dispatch(setFilters({
      search: searchInput.trim(),
      groupId: selectedGroupId ? parseInt(selectedGroupId) : undefined,
      level: selectedLevel || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      canceled: statusFilter === 'all' ? undefined : statusFilter === 'canceled'
    }));
    loadLessons(1); // Reset to page 1 when searching
  }, [dispatch, searchInput, selectedGroupId, selectedLevel, dateFrom, dateTo, statusFilter, loadLessons]);

  const handleClearFilters = useCallback(() => {
    setSearchInput('');
    setSelectedGroupId('');
    setSelectedLevel('');
    setDateFrom('');
    setDateTo('');
    setStatusFilter('all');
    dispatch(clearFilters());
    // Reload lessons without any filters
    dispatch(fetchLessonsAsync({}));
  }, [dispatch]);

  const handleDateFromChange = useCallback((newDateFrom: string) => {
    setDateFrom(newDateFrom);
  }, []);

  const handleDateToChange = useCallback((newDateTo: string) => {
    setDateTo(newDateTo);
  }, []);

  const formatDate = useCallback((dateString: string) => {
    return formatDateEuropean(dateString);
  }, []);

  // Get unique groups from lessons for the dropdown
  const getUniqueGroups = useCallback(() => {
    const groupsMap = new Map();
    lessons.forEach(lesson => {
      if (lesson.group) {
        groupsMap.set(lesson.group.id, lesson.group);
      }
    });
    return Array.from(groupsMap.values());
  }, [lessons]);

  const uniqueGroups = getUniqueGroups();

  // Apply client-side filters only for search and level (backend handles date and group)
  const getFilteredLessons = useCallback(() => {
    let filtered = lessons;

    if (filters.search) {
      filtered = filtered.filter(lesson => 
        lesson.topic?.toLowerCase().includes(filters.search.toLowerCase()) ||
        lesson.group?.name?.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    if (filters.level) {
      filtered = filtered.filter(lesson => lesson.group?.level === filters.level);
    }

    return filtered;
  }, [lessons, filters]);

  const filteredLessons = getFilteredLessons();

  const handlePageChange = useCallback((newPage: number) => {
    dispatch(setPage(newPage));
    loadLessons(newPage);
  }, [dispatch, loadLessons]);

  if (loading && lessons.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Lessons</h1>
          <p>Manage daily lessons and vocabulary</p>
        </div>
        <button 
          onClick={() => navigate(`/lesson?date=${new Date().toISOString().split('T')[0]}`)}
          className="btn-primary"
        >
          Create Lesson
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="filter-bar">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by topic or group..."
          className="search-input"
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />

        <label htmlFor="date-from-filter" className="filter-label">From</label>
        <input
          id="date-from-filter"
          type="date"
          value={dateFrom}
          onChange={(e) => handleDateFromChange(e.target.value)}
          className="form-input"
          placeholder="Optional"
        />

        <label htmlFor="date-to-filter" className="filter-label">To</label>
        <input
          id="date-to-filter"
          type="date"
          value={dateTo}
          onChange={(e) => handleDateToChange(e.target.value)}
          className="form-input"
          placeholder="Optional"
        />

        <select
          value={selectedGroupId}
          onChange={(e) => setSelectedGroupId(e.target.value)}
          className="form-select"
        >
          <option value="">All Groups</option>
          {uniqueGroups.map(group => (
            <option key={group.id} value={group.id}>
              {group.name || `${group.level.toUpperCase()} Group`}
            </option>
          ))}
        </select>

        <select
          value={selectedLevel}
          onChange={(e) => setSelectedLevel(e.target.value)}
          className="form-select"
        >
          <option value="">All Levels</option>
          {levels.map(level => (
            <option key={level.value} value={level.value}>
              {level.label}
            </option>
          ))}
        </select>

        <div className="lesson-status-toggle lesson-status-toggle--filter" role="group" aria-label="Lesson status filter">
          {(['all', 'active', 'canceled'] as const).map((status) => (
            <button
              key={status}
              type="button"
              className={`lesson-status-toggle__btn ${status === 'canceled' ? 'lesson-status-toggle__btn--canceled' : ''} ${statusFilter === status ? 'is-active' : ''}`}
              onClick={() => setStatusFilter(status)}
            >
              {status === 'all' ? 'All' : status === 'active' ? 'Active' : 'Canceled'}
            </button>
          ))}
        </div>

        <button onClick={handleSearch} className="btn-primary">
          Search
        </button>

        <button onClick={handleClearFilters} className="btn-secondary">
          Clear
        </button>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <p>
          Showing {filteredLessons.length} of {pagination.totalItems} lessons
          {(filters.dateFrom || filters.dateTo) && (
            <>
              {' '}from{' '}
              {filters.dateFrom ? formatDate(filters.dateFrom) : 'beginning'}
              {' '}to{' '}
              {filters.dateTo ? formatDate(filters.dateTo) : 'end'}
            </>
          )}
          {filters.search && ` matching "${filters.search}"`}
          {filters.level && ` at ${filters.level.toUpperCase()} level`}
          {filters.groupId && (
            <>
              {' '}in{' '}
              {uniqueGroups.find(g => g.id === filters.groupId)?.name || 
               `${uniqueGroups.find(g => g.id === filters.groupId)?.level.toUpperCase()} Group`}
            </>
          )}
        </p>
      </div>

      {/* Lessons Table */}
      <div className="students-table-container lessons-table">
        {filteredLessons.length === 0 ? (
          <div className="no-students">
            <p>No lessons found{(filters.dateFrom || filters.dateTo) ? ' for this period' : ''}.</p>
            <button 
              onClick={() => navigate(`/lesson?date=${new Date().toISOString().split('T')[0]}`)}
              className="btn-primary"
            >
              Create lesson for today
            </button>
          </div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>
                <th>Level</th>
                <th>Group</th>
                <th>Topic</th>
                <th>Date</th>
                <th>Vocabulary</th>
                <th>Link</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLessons.map((lesson) => (
                <tr key={lesson.id} className={lesson.canceled ? 'lesson-row--canceled' : ''}>
                  <td>
                    {lesson.group ? lesson.group.level.toUpperCase() : "-"}
                  </td>
                  <td>
                    {lesson.group ? (
                      lesson.group.name || `Группа ${lesson.group.level.toUpperCase()}`
                    ) : (
                      "Unknown Group"
                    )}
                  </td>
                  <td>
                    {lesson.topic || "No topic"}
                  </td>
                  <td>
                    {formatDate(lesson.date)}
                    {lesson.canceled && (
                      <span className="badge-canceled" style={{ marginLeft: '8px' }}>Canceled</span>
                    )}
                  </td>
                  <td>
                    {lesson.vocabulary && lesson.vocabulary.length > 0 ? (
                      `${lesson.vocabulary.length} words`
                    ) : (
                      "No vocabulary"
                    )}
                  </td>
                  <td>
                    {lesson.link ? (
                      <a href={lesson.link} target="_blank" rel="noopener noreferrer">
                        View Link
                      </a>
                    ) : (
                      "No link"
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => navigate(`/lesson?date=${lesson.date}&group=${lesson.groupId}`)}
                        className="btn-icon"
                        data-tooltip="Edit lesson"
                      >
                        ✎
                      </button>
                      <button
                        onClick={() => setDeleteModal({
                          isOpen: true,
                          lessonId: lesson.id,
                          lessonInfo: `${lesson.group?.name || lesson.group?.level.toUpperCase() || 'Unknown'} — ${formatDate(lesson.date)}${lesson.topic ? ` — ${lesson.topic}` : ''}`
                        })}
                        className="btn-icon"
                        data-tooltip="Delete lesson"
                        style={{ color: '#ef4444' }}
                      >
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination Controls */}
      {pagination.totalPages > 1 && (
        <div className="pagination-controls" style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          gap: '1rem', 
          marginTop: '2rem',
          padding: '1rem'
        }}>
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1 || loading}
            className="btn-secondary"
            style={{ minWidth: '80px' }}
          >
            Previous
          </button>
          <span style={{ fontWeight: '500' }}>
            Page {pagination.currentPage} of {pagination.totalPages}
          </span>
          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages || loading}
            className="btn-secondary"
            style={{ minWidth: '80px' }}
          >
            Next
          </button>
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        title="Delete Lesson"
        message={`Are you sure you want to delete this lesson?\n\n${deleteModal.lessonInfo}\n\nThis will also delete all vocabulary and absence records associated with this lesson. This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        dangerous
        onConfirm={async () => {
          if (deleteModal.lessonId) {
            try {
              await dispatch(deleteLessonAsync(deleteModal.lessonId)).unwrap();
            } catch (e) {
              // error handled by slice
            }
          }
          setDeleteModal({ isOpen: false, lessonId: null, lessonInfo: '' });
        }}
        onCancel={() => setDeleteModal({ isOpen: false, lessonId: null, lessonInfo: '' })}
      />

      {loading && lessons.length > 0 && (
        <div className="loading-overlay">
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

export default Lessons; 